import { Component, findComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import addComponentAnimation from '@swisslife/shared-ui/src/utils/animations/js/animations';
import { Image } from '@swisslife/patterns-base/src/atoms/image/js/image';
import '@swisslife/patterns-base/src/atoms/image/js/image';
import '../../collapse/js/collapse';
import { ISlickOptions } from '@swisslife/shared-types/src/types/slick-carousel';

enum Selectors {
	Slider = '.js-m-image-gallery__slider',
	SliderItem = '.js-m-image-gallery__slider-item',
	ListItem = '.js-m-image-gallery__list-item',
	SliderPrevButton = '.js-m-image-gallery__slider-buttons-prev',
	SliderNextButton = '.js-m-image-gallery__slider-buttons-next',
}

enum States {
	PreInit = 'state-m-image-gallery--pre-init',
	DescriptionVisible = 'state-m-image-gallery--description-visible',
}

// The @Component decorator will connect the class with `data-g-name="ImageGallery"` elements.
@Component('ImageGallery')
export class ImageGallery extends GondelJqueryComponent {
	private controllers: any[] = Array();
	private $slider: any;
	private $sliderItems: JQuery;
	private sliderHasBeenInitialized: boolean = false;

	public start() {
		this.$slider = this.$ctx.find(Selectors.Slider);
		this.$sliderItems = this.$ctx.find(Selectors.SliderItem);

		if (this.$slider.length > 0) {
			this.addImageLoadedHandler();
		}
	}

	public async sync() {
		if (this.$slider.length > 0) {
			this.controllers = await addComponentAnimation(this.$ctx);

			this.initImageGallery();
		} else {
			this.controllers = await addComponentAnimation(this.$ctx, Selectors.ListItem);
		}
	}

	public stop() {
		for (const controller of this.controllers) {
			controller.destroy(true);
		}

		if (this.sliderHasBeenInitialized) {
			this.$slider.slick('unslick');
		}
	}

	private async initImageGallery() {
		// tslint:disable-next-line
		await import(/* webpackChunkName: "dynamic-imports/slider-lib" */ '@swisslife/shared-ui/src/utils/slider/js/slider-lib');

		const slickOptions: ISlickOptions = {
			adaptiveHeight: true,
			slidesToShow: 1,
			slidesToScroll: 1,
			infinite: true,
			rows: 0, // https://github.com/kenwheeler/slick/issues/3110
			prevArrow: this.$ctx.find(Selectors.SliderPrevButton),
			nextArrow: this.$ctx.find(Selectors.SliderNextButton),
		};

		this.$slider = (this.$slider as any).slick(slickOptions);

		this.$slider.on('afterChange', this.handleSlideAfterChange);

		this.$ctx.removeClass(States.PreInit);
		this.sliderHasBeenInitialized = true;
		this.showDescriptionBox(0);
	}

	private addImageLoadedHandler() {
		const imageComponents = findComponents<Image>(this._ctx, 'Image');
		if (imageComponents.length > 0) {
			for (const imageComponent of imageComponents) {
				const promise = imageComponent.getImageLoadedPromise();
				promise.then(() => {
					this.refreshSlider();
				});
			}
		}
	}

	private refreshSlider() {
		// only act if the slider has actually been initialized
		if (this.sliderHasBeenInitialized) {
			// refresh slick when an image has been loaded to update the cloned slides and prevent image jumping
			this.$slider.slick('refresh');
		}
	}

	private handleSlideAfterChange = (event, slick, currentSlide) => {
		this.showDescriptionBox(currentSlide);
	};

	private showDescriptionBox(slideIndex) {
		this.$ctx.find(Selectors.SliderItem).removeClass(States.DescriptionVisible);
		const $slide = this.$ctx.find(`${Selectors.SliderItem}[data-slick-index="${slideIndex}"]`);

		$slide.addClass(States.DescriptionVisible);
	}
}
